import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, Users, MousePointer, Smartphone, Monitor, Layers } from "lucide-react";

interface AnalyticsStatsCardsProps {
  stats?: {
    totalPageViews: number;
    uniqueUsers: number;
    avgPagesPerSession: number;
    totalSessions: number;
    totalClicks: number;
    totalFeatureUsage: number;
    mobileUsers: number;
    desktopUsers: number;
  };
  isLoading: boolean;
}

export const AnalyticsStatsCards = ({ stats, isLoading }: AnalyticsStatsCardsProps) => {
  const cards = [
    {
      title: "Page Views",
      value: stats?.totalPageViews || 0,
      icon: Eye,
      description: "Total page views",
    },
    {
      title: "Unique Users",
      value: stats?.uniqueUsers || 0,
      icon: Users,
      description: "Logged-in users",
    },
    {
      title: "Sessions",
      value: stats?.totalSessions || 0,
      icon: Layers,
      description: `${stats?.avgPagesPerSession || 0} pages/session avg`,
    },
    {
      title: "Clicks",
      value: stats?.totalClicks || 0,
      icon: MousePointer,
      description: "Button clicks tracked",
    },
    {
      title: "Feature Usage",
      value: stats?.totalFeatureUsage || 0,
      icon: Layers,
      description: "Feature interactions",
    },
    {
      title: "Device Split",
      value: `${stats?.mobileUsers || 0} / ${stats?.desktopUsers || 0}`,
      icon: Smartphone,
      description: "Mobile / Desktop",
      secondaryIcon: Monitor,
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 mb-1" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <div className="flex items-center gap-1">
              <card.icon className="h-4 w-4 text-muted-foreground" />
              {card.secondaryIcon && (
                <card.secondaryIcon className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
